
import { mapState } from "vuex";

import { debounce } from "~/utils/underscore-utils";
import siteConfig from "~/site.config.json";

const SEARCH_DEBOUNCE_DELAY = 300;

export default {
  name: "SearchBarCenter",

  props: {
    isSidebarOpen: { type: Boolean, default: false },
  },

  data() {
    return {
      suggestion: null,
      suggestions: [],
      redirect: "",
      currentQuery: "",
      products: [],
      blogs: [],
      loading: false,
      emptyStateLoading: false,
      debouncedSearch: () => {},
      sizesForUrl: siteConfig?.sizesForUrl ?? [],
      popularSearches: [],
      trendingProducts: [],
    };
  },

  computed: {
    ...mapState(["siteConfig"]),
    ...mapState("app", ["isSearchOpen", "activeMenuIndex"]),
  },

  created() {
    this.debouncedSearch = debounce(this.search, SEARCH_DEBOUNCE_DELAY);
  },

  beforeDestroy() {
    // Cancel any pending debounced calls
    if (this.debouncedSearch?.cancel) {
      this.debouncedSearch.cancel();
    }
  },

  methods: {
    closeSearch() {
      const payload = {
        title: [{ text: "Search" }],
        url: "from search",
      };

      this.$store.dispatch("app/setRedirectFrom", payload);
      this.$store.dispatch("app/closeSearch", this.isTablet);
      if (this.isSidebarOpen && this.activeMenuIndex > -1) {
        this.$store.dispatch("app/setActiveMenuIndex", -1);
      }
      this.currentQuery = "";
    },

    async search() {
      try {
        this.loading = true;
        const [productsData, blogsData] = await Promise.all([
          this.$search.productsAutocomplete(this.currentQuery, 6),
          this.$search.blogsAutocomplete(this.currentQuery, 3),
        ]);

        this.blogs = (blogsData.data.results ?? []).map(blog =>
          this.getBlogProps(blog)
        );
        this.products = (productsData.data.results ?? []).map(product =>
          this.getProductProps(product)
        );
        this.redirect = productsData.data.merchandising.redirect;
        this.suggestion = productsData.suggestions[0];
        this.suggestions = productsData.suggestions;
      } catch (err) {
        this.$bugsnag.notify(err, e => {
          e.context = "SearchBar search";
        });
      } finally {
        this.loading = false;
      }
    },

    handleInputChange(event) {
      this.currentQuery = event.currentTarget.value;

      this.$store.dispatch("app/openSearch", this.isTablet);

      if (this.currentQuery === "") {
        this.loading = false;
        this.fetchEmptySearchData();
        return;
      }

      this.loading = true;
      this.debouncedSearch();
    },

    handleFocus() {
      this.$store.dispatch("app/openSearch", this.isTablet);
      if (!this.currentQuery) {
        this.fetchEmptySearchData();
      }
    },

    handleSubmit(event) {
      const searchedValue = event.target.search.value;
      this.$gtm.gtagEvent("search", { search_term: searchedValue });

      if (!searchedValue) {
        return;
      }

      // Blur the input to hide keyboard on mobile
      if (this.$refs.searchInput) {
        this.$refs.searchInput.blur();
      }

      if (this.redirect || this.products.length > 0) {
        const sizes = this.extractSizesFromSuggestion(searchedValue);

        const location = this.redirect
          ? { path: this.redirect }
          : {
              path: "/products/search",
              query: {
                query: searchedValue,
                ...(sizes.length > 0 && {
                  "filter.option_sizes": sizes,
                }),
              },
            };

        this.$router.push(location);
        this.closeSearch();
      }
    },
    getBlogProps({ name: title, imageUrl: image, handle, ...rest }) {
      return {
        ...rest,
        title,
        image,
        url: `/blogs/inspiration/${handle}`,
      };
    },
    getProductProps({ handle, selectedVariant, ...rest }) {
      return {
        ...rest,
        url: `/products/${handle}?variant=${selectedVariant?.id}`,
      };
    },
    extractSizesFromSuggestion(text) {
      if (!text || !this.sizesForUrl.length) return [];

      const lower = text.toLowerCase();

      const detectedSizes = this.sizesForUrl
        .map(size => {
          const index = lower.indexOf(size.toLowerCase());
          return index !== -1 ? { size, index } : null;
        })
        .filter(Boolean)
        .sort((a, b) => a.index - b.index);

      // AC2.2: pick the first size as it appears in the query text
      return detectedSizes.length ? [detectedSizes[0].size] : [];
    },
    selectPopularSearch(value) {
      this.currentQuery = value;
      this.handleSubmit({ target: { search: { value } } });
    },
    async fetchTrendingProducts() {
      const response = await this.$search.recommend({
        tag: "personalized",
        limit: 6,
      });

      this.trendingProducts = (response?.data?.results ?? []).map(product => ({
        ...product,
        url: product.url || `/products/${product.handle}`,
      }));
    },
    async fetchPopularSearches() {
      const queries = await this.$search.getTrendingQueries(6);

      this.popularSearches = queries ?? [];
    },
    async fetchEmptySearchData() {
      try {
        this.emptyStateLoading = true;

        await Promise.all([
          this.fetchPopularSearches(),
          this.fetchTrendingProducts(),
        ]);
      } catch (err) {
        this.popularSearches = [];
        this.trendingProducts = [];

        this.$bugsnag.notify(err, e => {
          e.context = "Empty search data failed in SearchBar";
        });
      } finally {
        this.emptyStateLoading = false;
      }
    },
  },
};
